/**
 * Util for managing click-anywhere scenarios, like menus that close
 * when the user clicks anywhere on the page, except the menu element.
 *
 * Checks if event.target is the excluded element, or nested inside.
 * Seems more reliable than coordinate based checks.
 *
 * Overkill? Dunno how often I quickly hacked this sort of functionality
 * and thought there should really be a reusable util. :-D
 *
 * Use:
 *
 *    // Create
 *    this.clickAnywhere = new ClickAnywhere()
 *
 *    // Activate
 *    this.clickAnywhere.except( this.menuEl ).toCall( this.closeMenu )
 *
 *    // Turn off when done
 *    this.clickAnywhere.stop()
 *
 *
 * @TODO support multiple excluded elements
 *
 */
export default class ClickAnywhere {
  constructor(eventType = 'click') {
    this.eventType = eventType
    this.callback = null
    this.exceptElements = null
  }

  except(elements) {
    elements.forEach(el => { if (!el || !el.parentNode) console.error('ClickAnywhere invalid element:', el) })
    this.exceptElements = elements
    return this
  }

  toCall(callback) {
    this.callback = callback
    document.addEventListener(this.eventType, this.onClick, true) // Using capture, in case of stopPropagation() somewhere
    document.addEventListener('keydown', this.onClick, true) // Esc key

    // Focus event, intended for keyboard nav / accessibility, but have to
    // take it out for the time being - cause it triggers when for example
    // clicking a submit button outside, then before the click event on the
    // button can fire the focus event fires, which might close a menu, change the layout...
    // then the button has moved all of a sudden and nothing happened ... annyoing for a user. ;-)
    // @TODO think how this can be solved
    // document.addEventListener('focus', this.onClick, true)

    return this
  }

  stop() {
    if (this.callback) {
      document.removeEventListener(this.eventType, this.onClick, true)
      document.removeEventListener('focus', this.onClick, true)
      this.callback = null
      this.exceptElements = null
    }
  }

  onClick = (e) => {
    if (!this.callback) { // IE11 oddness - called after removeEventListener
      return
    }

    let isAnywhere = false

    if (e.type === 'keydown') {
      isAnywhere = (e.key === 'Escape')
    } else if (!this.isExceptElement(e.target)) {
      isAnywhere = true
    }

    if (isAnywhere) {
      this.callback(e)
    }
  }

  isExceptElement(el) {
    for (let i = 0; i < this.exceptElements.length; i++) {
      const exceptEl = this.exceptElements[i]

      if (exceptEl.contains(el)) {
        return true
      }
    }
    return false
  }
}
